import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { availabilityService } from '@/services/availabilityService';

export const useAvailability = () => {
  return useQuery({
    queryKey: ['availability'],
    queryFn: () => availabilityService.list(),
    staleTime: 5 * 60 * 1000,
  });
};

export const useAvailabilityItem = (id: string | number | undefined) => {
  return useQuery({
    queryKey: ['availability', id],
    queryFn: () => availabilityService.get(id!),
    enabled: !!id,
    staleTime: 5 * 60 * 1000,
  });
};

export const useAvailableSlots = (date: string | undefined) => {
  return useQuery({
    queryKey: ['availableSlots', date],
    queryFn: () => availabilityService.getAvailableSlots(date!),
    enabled: !!date,
    staleTime: 1 * 60 * 1000,
  });
};

export const useCreateAvailability = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (availabilityData: Record<string, unknown>) =>
      availabilityService.create(availabilityData),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['availability'] });
      queryClient.invalidateQueries({ queryKey: ['availableSlots'] });
    },
  });
};

export const useUpdateAvailability = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }: { id: string | number; data: Record<string, unknown> }) =>
      availabilityService.update(id, data),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['availability'] });
      queryClient.invalidateQueries({ queryKey: ['availability', variables.id] });
      queryClient.invalidateQueries({ queryKey: ['availableSlots'] });
    },
  });
};

export const useDeleteAvailability = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string | number) => availabilityService.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['availability'] });
      queryClient.invalidateQueries({ queryKey: ['availableSlots'] });
    },
  });
};
